import { useEffect, useRef, useState } from "react";
import { ActionCard } from "./ActionCard";
import { Markdown } from "./Markdown";
import { ReviewSummaryCard } from "./ReviewSummaryCard";
import { useChat, useWsStatus } from "./useChat";

/** Etykieta odznaki wiadomości zainicjowanej przez system (ceremonia / przegląd). */
const BADGE_LABEL: Record<string, string> = {
  review: "przegląd",
  weekly_review: "przegląd tygodniowy",
  someday_review: "kiedyś/może",
  daily_digest: "poranny digest",
  inbox_aging: "inbox",
  tickler: "tickler",
  deadlines: "terminy",
  reminders: "przypomnienie",
  stalled_projects: "projekty",
  waiting_followup: "waiting",
};

const STATUS_LABEL = {
  connecting: "łączę…",
  open: "",
  closed: "brak połączenia — ponawiam",
} as const;

export function ChatPage() {
  const { items, streaming, loading, hasMore, loadOlder, send, confirm, reject, undo, busyAction } =
    useChat();
  const status = useWsStatus();
  const [draft, setDraft] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [items, streaming]);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    send(draft);
    setDraft("");
  };

  return (
    <div className="chat">
      {status !== "open" && <div className="chat__status">{STATUS_LABEL[status]}</div>}

      <div className="chat__scroll">
        {hasMore && (
          <button className="btn chat__more" onClick={() => void loadOlder()}>
            Wczytaj starsze
          </button>
        )}

        {loading && <p className="muted">Wczytuję rozmowę…</p>}
        {!loading && items.length === 0 && (
          <p className="muted chat__empty">
            Napisz, co masz na głowie. Wrzucę to do inboxu i pomogę rozstrzygnąć.
          </p>
        )}

        {items.map((item) => {
          if (item.kind === "action") {
            return (
              <ActionCard
                key={item.id}
                card={item.card}
                busy={busyAction === item.id}
                onConfirm={confirm}
                onReject={reject}
                onUndo={undo}
              />
            );
          }
          if (item.kind === "review") {
            return <ReviewSummaryCard key={item.id} session={item.session} />;
          }
          if (item.kind === "note") {
            return (
              <div key={item.id} className="chat__note">
                {item.text}
              </div>
            );
          }
          if (item.kind === "error") {
            return (
              <div key={item.id} className="chat__error">
                {item.text}
              </div>
            );
          }
          return (
            <div
              key={item.id}
              className={`bubble bubble--${item.role}${
                item.ceremony === "review" ? " bubble--review" : ""
              }`}
            >
              {item.ceremony && (
                <span className="bubble__ceremony">
                  {BADGE_LABEL[item.ceremony] ?? item.ceremony}
                </span>
              )}
              {item.role === "assistant" ? <Markdown text={item.text} /> : item.text}
            </div>
          );
        })}

        {streaming !== null && (
          <div className="bubble bubble--assistant bubble--streaming">
            {streaming ? <Markdown text={streaming} /> : <span className="dots">…</span>}
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      <form className="chat__composer" onSubmit={submit}>
        <textarea
          className="chat__input"
          value={draft}
          rows={1}
          placeholder="Co masz na głowie?"
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              submit(event);
            }
          }}
        />
        <button className="btn btn--primary" type="submit" disabled={!draft.trim()}>
          Wyślij
        </button>
      </form>
    </div>
  );
}
